// Minimal STORED (uncompressed) zip container — the outer envelope of an .xlsx.
//
// Zero dependencies, on purpose: the mini-app cannot add npm packages (CLAUDE.md), and the
// workbook is ~50-100 KB of XML, so compression buys nothing worth a dependency. STORED is a
// first-class zip method that Excel, Google Sheets, Numbers and LibreOffice all read.
//
// Two things Excel is picky about, both avoided here: data descriptors (general-purpose flag
// bit 3, used by streaming writers that don't know sizes up front) trigger "unreadable content",
// and zip64 is unnecessary far below 4 GB. Sizes and CRCs are known before writing, so the
// layout is the classic local-header / data / central-directory / end-of-central-directory.
//
// `now` is injected rather than read from the clock so byte-identical output is testable.

export interface ZipEntry {
  /** Forward-slash path inside the archive, no leading slash (e.g. 'xl/workbook.xml'). */
  name: string;
  data: Uint8Array;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

/** IEEE 802.3 CRC-32 (the zip/PNG/gzip polynomial), unsigned. */
export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * MS-DOS date/time fields (local time, 2-second resolution). Years before 1980 are clamped to
 * the epoch of the format, 1980-01-01 00:00:00.
 */
export function dosDateTime(d: Date): { time: number; date: number } {
  if (d.getFullYear() < 1980) return { time: 0, date: (0 << 9) | (1 << 5) | 1 };
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time, date };
}

const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;
const VERSION_NEEDED = 20; // 2.0 — plain deflate/stored features only
const VERSION_MADE_BY = 0x0014; // 2.0, MS-DOS attribute convention
const FLAG_UTF8_NAMES = 0x0800;
const METHOD_STORED = 0;
const LOCAL_HEADER_LEN = 30;
const CENTRAL_HEADER_LEN = 46;
const EOCD_LEN = 22;

/** Build a STORED zip. Entries are written in the order given. */
export function buildStoredZip(entries: ZipEntry[], now: Date): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const { time, date } = dosDateTime(now);
  const prepared = entries.map((e) => ({
    nameBytes: enc.encode(e.name),
    data: e.data,
    crc: crc32(e.data),
  }));

  const localTotal = prepared.reduce((n, p) => n + LOCAL_HEADER_LEN + p.nameBytes.length + p.data.length, 0);
  const centralTotal = prepared.reduce((n, p) => n + CENTRAL_HEADER_LEN + p.nameBytes.length, 0);
  const out = new Uint8Array(localTotal + centralTotal + EOCD_LEN);
  const view = new DataView(out.buffer);
  let pos = 0;
  const u16 = (v: number) => {
    view.setUint16(pos, v, true);
    pos += 2;
  };
  const u32 = (v: number) => {
    view.setUint32(pos, v >>> 0, true);
    pos += 4;
  };
  const bytes = (b: Uint8Array) => {
    out.set(b, pos);
    pos += b.length;
  };

  const offsets: number[] = [];
  for (const p of prepared) {
    offsets.push(pos);
    u32(SIG_LOCAL);
    u16(VERSION_NEEDED);
    u16(FLAG_UTF8_NAMES);
    u16(METHOD_STORED);
    u16(time);
    u16(date);
    u32(p.crc);
    u32(p.data.length);
    u32(p.data.length);
    u16(p.nameBytes.length);
    u16(0); // extra field length
    bytes(p.nameBytes);
    bytes(p.data);
  }

  const centralStart = pos;
  prepared.forEach((p, i) => {
    u32(SIG_CENTRAL);
    u16(VERSION_MADE_BY);
    u16(VERSION_NEEDED);
    u16(FLAG_UTF8_NAMES);
    u16(METHOD_STORED);
    u16(time);
    u16(date);
    u32(p.crc);
    u32(p.data.length);
    u32(p.data.length);
    u16(p.nameBytes.length);
    u16(0); // extra field length
    u16(0); // file comment length
    u16(0); // disk number start
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(offsets[i]);
    bytes(p.nameBytes);
  });

  const centralSize = pos - centralStart;
  u32(SIG_EOCD);
  u16(0); // this disk
  u16(0); // disk with central directory
  u16(prepared.length);
  u16(prepared.length);
  u32(centralSize);
  u32(centralStart);
  u16(0); // comment length
  return out;
}
